import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const TaxReportGenerator = ({ transactions, taxCalculations, taxYear, filingStatus }) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedScope, setSelectedScope] = useState('full');
  const [isGenerating, setIsGenerating] = useState(false);

  const formatOptions = [
    { value: 'pdf', label: 'PDF Report' },
    { value: 'csv', label: 'CSV Data Export' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'form8949', label: 'Form 8949 (PDF)' },
    { value: 'schedule_d', label: 'Schedule D (PDF)' }
  ];

  const scopeOptions = [
    { value: 'full', label: 'Complete Tax Report' },
    { value: 'summary', label: 'Tax Summary Only' },
    { value: 'transactions', label: 'Transaction Details' },
    { value: 'realized', label: 'Realized Gains/Losses Only' },
    { value: 'crypto', label: 'Cryptocurrency Only' },
    { value: 'stocks', label: 'Stocks Only' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(amount);
  };

  const getReportDescription = (format, scope) => {
    const descriptions = {
      pdf: 'Professional PDF report with charts and analysis',
      csv: 'Raw transaction data in CSV format for further analysis',
      excel: 'Formatted Excel workbook with multiple sheets',
      form8949: 'IRS Form 8949 for capital gains and losses',
      schedule_d: 'IRS Schedule D for capital gains and losses summary'
    };
    return descriptions?.[format] || 'Generate detailed tax report';
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      // Simulate report generation delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real implementation, this would:
      // 1. Call backend API to generate report
      // 2. Process transactions and tax calculations
      // 3. Generate file in selected format
      // 4. Download or display the report
      
      const reportData = {
        format: selectedFormat,
        scope: selectedScope,
        taxYear,
        filingStatus,
        transactions: transactions?.filter(t => {
          if (selectedScope === 'realized') return t?.realized;
          if (selectedScope === 'crypto') return t?.assetType === 'crypto';
          if (selectedScope === 'stocks') return t?.assetType === 'stock';
          return true;
        }),
        taxCalculations,
        generatedAt: new Date()?.toISOString()
      };
      
      // Mock download
      console.log('Generated tax report:', reportData);
      
      // Simulate file download
      const filename = `tax-report-${taxYear}-${selectedFormat}.${selectedFormat}`;
      alert(`Tax report "${filename}" would be downloaded here in a real implementation.`);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const reportStats = {
    totalTransactions: transactions?.length || 0,
    realizedTransactions: transactions?.filter(t => t?.realized)?.length || 0,
    cryptoTransactions: transactions?.filter(t => t?.assetType === 'crypto')?.length || 0,
    stockTransactions: transactions?.filter(t => t?.assetType === 'stock')?.length || 0
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="FileText" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-card-foreground">Generate Tax Report</h2>
      </div>

      {/* Report Configuration */}
      <div className="space-y-4 mb-6">
        <Select
          label="Report Format"
          placeholder="Select report format"
          options={formatOptions}
          value={selectedFormat}
          onChange={setSelectedFormat}
        />

        <Select
          label="Report Scope"
          placeholder="Select report scope"
          options={scopeOptions}
          value={selectedScope}
          onChange={setSelectedScope}
        />

        <div className="p-3 bg-muted/50 rounded-md">
          <p className="text-sm text-muted-foreground">
            {getReportDescription(selectedFormat, selectedScope)}
          </p>
        </div>
      </div>

      {/* Report Preview Stats */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-card-foreground mb-3">Report Preview</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Transactions:</span>
            <span className="font-medium text-card-foreground">{reportStats?.totalTransactions}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Realized:</span>
            <span className="font-medium text-card-foreground">{reportStats?.realizedTransactions}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Crypto:</span>
            <span className="font-medium text-card-foreground">{reportStats?.cryptoTransactions}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Stocks:</span>
            <span className="font-medium text-card-foreground">{reportStats?.stockTransactions}</span>
          </div>
        </div>
      </div>

      {/* Tax Summary in Report */}
      <div className="mb-6 p-4 bg-primary/5 border border-primary/10 rounded-md">
        <h3 className="text-sm font-medium text-card-foreground mb-3">Tax Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Net Short-Term:</span>
            <span className={`font-medium ${
              (taxCalculations?.netShortTerm || 0) >= 0 ? 'text-destructive' : 'text-success'
            }`}>
              {formatCurrency(taxCalculations?.netShortTerm || 0)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Net Long-Term:</span>
            <span className={`font-medium ${
              (taxCalculations?.netLongTerm || 0) >= 0 ? 'text-warning' : 'text-success'
            }`}>
              {formatCurrency(taxCalculations?.netLongTerm || 0)}
            </span>
          </div>
          <div className="flex justify-between pt-2 border-t border-primary/10">
            <span className="text-card-foreground font-medium">Estimated Tax:</span>
            <span className="font-bold text-primary">
              {formatCurrency(taxCalculations?.estimatedTax || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <Button
        onClick={handleGenerateReport}
        disabled={isGenerating || reportStats?.totalTransactions === 0}
        className="w-full"
      >
        {isGenerating ? (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>
            <span>Generating Report...</span>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <Icon name="Download" size={16} />
            <span>Generate {selectedFormat?.toUpperCase()} Report</span>
          </div>
        )}
      </Button>

      {/* Report Features */}
      <div className="mt-6 space-y-3">
        <h3 className="text-sm font-medium text-card-foreground">Report Features</h3>
        <div className="space-y-2">
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="CheckCircle" size={14} className="text-success" />
            <span className="text-muted-foreground">Detailed transaction history</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="CheckCircle" size={14} className="text-success" />
            <span className="text-muted-foreground">Gain/loss calculations</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="CheckCircle" size={14} className="text-success" />
            <span className="text-muted-foreground">Tax optimization suggestions</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="CheckCircle" size={14} className="text-success" />
            <span className="text-muted-foreground">IRS-compliant formatting</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="CheckCircle" size={14} className="text-success" />
            <span className="text-muted-foreground">Year-end tax planning insights</span>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 p-3 bg-warning/10 rounded-md">
        <div className="flex items-start space-x-2">
          <Icon name="AlertTriangle" size={14} className="text-warning mt-0.5" />
          <div>
            <p className="text-xs text-muted-foreground">
              <strong>Tax Disclaimer:</strong> This report is for informational purposes only. 
              Please consult with a qualified tax professional before filing your tax return.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxReportGenerator;